import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledH2, StyledLabel } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { uuid } from "uuidv4";

import { store } from "@components/VerticalLayout/store";
import { setIsLoading, setKey } from "./store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../api/hooks";
import ErrorToast from "@components/Common/ErrorTost";
import { showSuccessToast } from "@components/Common/SuccessToast";

type ConfirmationArchiveType = {
    setIsModalOpened: Function;
    isModalOpened: boolean;
    e?: any;
    setDataIsUpdated: Function;
    selectedRows: any;
};

export function ConfirmationArchive({
    setIsModalOpened,
    isModalOpened,
    setDataIsUpdated,
    e,
    selectedRows,
}: ConfirmationArchiveType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { franchiseID, userID, operatorID } = useSnapshot(store);
    const uuidUser = localStorage.getItem("uuidUser");

    const getArrayOfRows = () => {
        let arrayOfRows: any[] = [];
        let ids: any[] = [];

        selectedRows.forEach(async (el: any) => {
            if (
                el[t("Shop")] === undefined &&
                ids.indexOf(`${el.idShop}${el.id}`) === -1
            ) {
                arrayOfRows.push(el);
                ids.push(`${el.idShop}${el.id}`);
            }
        });
        arrayOfRows = [...new Set(arrayOfRows)];
        return arrayOfRows;
    };

    async function archiveDevise() {
        setIsLoading(true);
        toast.dismiss();
        try {
            const rowsToArchive =
                selectedRows.length !== 0 ? getArrayOfRows() : [e.selectedRow];

            const allShopIds = [
                ...new Set(rowsToArchive.map((row: any) => row.idShop)),
            ];

            const devisesData: Record<string, any> = {};

            rowsToArchive.forEach((row: any) => {
                devisesData[row.id] = {
                    Nom_d_Affichage: row[t("Display name")],
                    tag: row[t("Tag")],
                    symbole: row[t("Symbol")],
                    Nbr_décimale: row[t("Nbr decimal")],
                    designation_décimale: row[t("Decimal designation")],
                    devise: row[t("Currency")],
                    pays: row[t("Country")],
                    shopId: row.idShop,
                    isActive: false,
                    isArchived: true,
                };
            });

            const payload = {
                userId: userID,
                shopId: allShopIds,
                franchiseId: franchiseID,
                isShop: false,
                operatorId: operatorID,
                uuidUser: uuidUser,
                data: devisesData,
            };

            const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/devise`;
            const response: any = await fetchGlobal(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (response.error) {
                throw new Error(response.message || "Archive failed");
            }

            const count = rowsToArchive.length;
            showSuccessToast(
                count === 1
                    ? t("The currency has been successfully archived")
                    : t(`${count} currencies have been successfully archived`)
            );
            setDataIsUpdated?.(true);
            setKey(uuid());
        } catch (error) {
            ErrorToast(t("There's an error"));
        } finally {
            setIsLoading(false);
        }
    }
    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsModalOpened(!isModalOpened);
                    setDataIsUpdated!(true);
                }}
            >
                <StyledH2 className="text-uppercase">{t("Alert")}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        "Attention, note that this currency(s) can be used by other modules.\n Do you want to confirm the archiving of the selected currency(s)?"
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                        setIsLoading(false);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        archiveDevise();
                        setIsModalOpened(!isModalOpened);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
